/**
 * 统一内联 SVG 图标集（21d）。
 *
 * 旧版全程用 **Material Icons**（`Icons.add_circle_outline / alternate_email /
 * sentiment_satisfied_outlined / mic / photo_library / file_present / person_add …`），
 * 新版此前是**字符与 emoji 凑的**（😊🖼📎🎤×›‹✓），两端观感差得最明显的就是这里。
 * 这里按旧版用到的 Material 图形**自己画描边 SVG**——不引图标库（PROMPT：不引 UI 库，
 * 且字体图标包会把首屏预算吃光），也不下位图（高 DPI 要么糊要么多下一份资源）。
 *
 * 口径：
 * - 统一 24×24 viewBox、`currentColor` 描边、1.8 线宽——与 meIcons/navRail 同一套手法，
 *   颜色随 CSS 走，主题切换零成本。
 * - **按用途分模块，不建一个大 barrel**：打包器把「被首屏和懒块共同引用的模块」
 *   整个放进首屏块，barrel 会让详情页的图标也进首屏预算。本模块只放**首屏真的要用的**
 *   （输入栏/会话列表/聊天头）；详情页专用的图标各自就近声明或另开模块。
 * - 尺寸由调用方用 CSS 控制（`width/height` 不写死在组件里），所以同一个图标在
 *   输入栏 22px、在托盘 24px 不需要两份。
 */
import type { JSX } from 'preact';

const BASE: JSX.SVGAttributes<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 1.8,
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
  'aria-hidden': 'true',
};

// `string | undefined` 而非 `string`：exactOptionalPropertyTypes 下 CSS Modules
// 的类名取值就是可空的，收窄成 string 会让每个调用点都要断言
type IconProps = { readonly class?: string | undefined };

/** add_circle_outline：输入栏「更多」⊕ */
export function IconPlusCircle({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 8.4v7.2M8.4 12h7.2" />
    </svg>
  );
}

/** alternate_email：输入栏「@成员」（仅群聊） */
export function IconAt({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="12" cy="12" r="3.4" />
      <path d="M15.4 8.6v4.6a2.4 2.4 0 0 0 4.8 0V12a8.2 8.2 0 1 0-3.3 6.6" />
    </svg>
  );
}

/** sentiment_satisfied_outlined：输入栏「表情与贴纸」 */
export function IconSmile({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M8.6 13.6a4 4 0 0 0 6.8 0" />
      <path d="M9.4 9.6v.1M14.6 9.6v.1" />
    </svg>
  );
}

/** mic：输入栏「录制语音」 */
export function IconMic({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <rect x="9.2" y="3.2" width="5.6" height="10.4" rx="2.8" />
      <path d="M6 11.4a6 6 0 0 0 12 0" />
      <path d="M12 17.4v3.4" />
    </svg>
  );
}

/** photo_library：更多托盘「相册」 */
export function IconPhotoLibrary({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <rect x="7.4" y="3.4" width="13.2" height="13.2" rx="2.4" />
      <path d="M16.6 20.6H5.8a2.4 2.4 0 0 1-2.4-2.4V7.4" />
      <circle cx="11.4" cy="8" r="1.4" />
      <path d="M20.6 13.4 17 10l-6.2 6.6" />
    </svg>
  );
}

/** file_present：更多托盘「文件」 */
export function IconFile({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M15.6 3.4H7.8a2 2 0 0 0-2 2v13.2a2 2 0 0 0 2 2h8.4a2 2 0 0 0 2-2V6.2z" />
      <path d="M15.2 3.6v3.6h3.6" />
    </svg>
  );
}

/** person_add：更多托盘「名片」 */
export function IconPersonAdd({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="10" cy="8.4" r="3.4" />
      <path d="M3.8 19.6c0-3.1 2.8-4.9 6.2-4.9 1 0 2 .16 2.9.47" />
      <path d="M17.4 14.4v5.2M14.8 17h5.2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 通用小图标（21d-3）：旧版这几处全是 Material 字形，新版此前拿 ‹ › ✓ × ★
// 这些**字符**凑——字符的粗细/基线随系统字体走，两端和旧版都对不齐，
// 这也是人工一开始说「图标跟原来不一样」最扎眼的一批。
// 它们同时被首屏（聊天页/输入栏）与懒块（各详情页）用到，所以就放在这个
// 首屏模块里：打包器本来也会把共用模块放进首屏块，拆出去只是多一个 chunk。
// ---------------------------------------------------------------------------

/** arrow_back_ios：返回钮的左尖角（BackButton 在用） */
export function IconChevronLeft({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M14.6 5.4 8 12l6.6 6.6" />
    </svg>
  );
}

/** chevron_right：列表行尾的「可进入」指示 */
export function IconChevronRight({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M9.4 5.4 16 12l-6.6 6.6" />
    </svg>
  );
}

/**
 * edit（铅笔）：群设置页头的「群管理」入口（21ah-6，仅管理员/群主可见）。
 * 旧版 Material edit_outlined 同形，按本套线性图标笔锋重画。
 */
export function IconEdit({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M4.8 19.2h3.1L18.3 8.8a1.8 1.8 0 0 0 0-2.5l-.6-.6a1.8 1.8 0 0 0-2.5 0L4.8 16.1z" />
      <path d="M14.2 7.2l2.6 2.6" />
    </svg>
  );
}

/**
 * megaphone：群公告（21ah 聊天页公告条）。旧版用 Material 的
 * campaign_outlined，这里按本套线性图标的笔锋重画（同 BASE 的 1.8 描边）。
 */
export function IconMegaphone({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      {/* 一条路径走完：喇叭口 → 左侧音箱体 → 回到起点（闭合） */}
      <path d="M19 5.5v13l-13-3.3a2 2 0 0 1 0-6.4z" />
    </svg>
  );
}

/** check：选中态对勾（单选 sheet / 多选行） */
export function IconCheck({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M4.8 12.6 9.6 17.4 19.2 6.9" />
    </svg>
  );
}

/** close：关闭/取消（浮层标题栏、引用条、选择条） */
export function IconClose({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

/** star（实心）：星标好友 */
export function IconStar({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls} fill="currentColor" stroke="none">
      <path d="M12 3.6l2.6 5.3 5.8.85-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.85z" />
    </svg>
  );
}

/** done_all：双勾（对方已读）。旧版气泡/会话行的已读标记就是这个 Material 字形 */
export function IconCheckAll({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M1.8 12.6 6 16.8 14.4 7.2" />
      <path d="M9.6 15.6 11.4 17.4 20.4 6.9" />
    </svg>
  );
}

/** schedule：发送中（时钟）。旧版 pending 态同字形 */
export function IconClock({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.4V12l3 1.8" />
    </svg>
  );
}

/** refresh_rounded：会话列表头「刷新」（21ab） */
export function IconRefresh({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M20.4 12a8.4 8.4 0 1 1-2.5-6" />
      <path d="M18.4 3.2v3.4h-3.4" />
    </svg>
  );
}

/** group_add_outlined：会话列表头「发起群聊」（21ab） */
export function IconGroupAdd({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <circle cx="9.4" cy="8.2" r="3.3" />
      <path d="M3.4 19.4c0-3 2.7-4.8 6-4.8s6 1.8 6 4.8" />
      <path d="M16.4 4.6a3.3 3.3 0 0 1 0 6.6" />
      <path d="M19.4 14.2v5.2M16.8 16.8h5.2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 阅后即焚 / 端到端加密（21af，人工指图：聊天头上那两个还是 emoji 🔥🔒，
// 与旁边已经画好的 ⓘ/× 不是一套——emoji 的字形、粗细、基线全随系统走，
// 两端和旧版都对不齐）。旧版用的是 Material `local_fire_department` 与
// `lock / lock_open`（chat_screen.dart:6506/6937），这里按同一语义画描边版。
// 聊天头、输入栏、气泡 meta 三处共用。
// ---------------------------------------------------------------------------

/** local_fire_department：阅后即焚（火焰 + 内焰） */
export function IconFlame({ class: cls }: IconProps = {}) {
  return (
    <svg {...BASE} class={cls}>
      <path d="M12 3c-.2 2.3-1.2 3.8-2.6 5.2-1.7 1.8-3 3.5-3 5.8a5.6 5.6 0 0 0 11.2 0c0-3.2-2.2-6.6-5.6-11Z" />
      <path d="M12 19.4a3 3 0 0 0 3-3c0-1.4-1-2.8-3-4.4-2 1.6-3 3-3 4.4a3 3 0 0 0 3 3Z" />
    </svg>
  );
}

/**
 * lock / lock_open：端到端加密（`open` 给未开态的开口锁梁）。两态**合成一个
 * 组件**——只差锁梁那一笔，拆两份白占首屏预算（实测首屏 JS 卡在 80KB 线上）。
 */
export function IconLock({ class: cls, open }: IconProps & { open?: boolean } = {}) {
  return (
    <svg {...BASE} class={cls}>
      <rect x="4.8" y="10.2" width="14.4" height="9.4" rx="2.2" />
      <path d={open ? 'M8.5 10.2V7.9a3.5 3.5 0 0 1 7 0' : 'M8.5 10.2V7.9a3.5 3.5 0 0 1 7 0v2.3'} />
      <path d="M12 13.8v2.2" />
    </svg>
  );
}
